import { ReactNode, useCallback, useMemo } from 'react';
import { usePimlico } from '@package/smart-wallet/hooks';
import { useMagic } from '@package/smart-wallet/providers';
import { useAuthStore } from '@package/smart-wallet/store';
import { clearAppCache } from '@package/smart-wallet/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useAccount, useConnect, useDisconnect } from 'wagmi';

import { WalletProviderContext } from './SmartWalletContext';

export const SmartWalletProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { logOut, isLoggedIn } = useAuthStore((state) => state);
  const { magic } = useMagic();
  const { isConnected: isConnectedWagmi, isConnecting } = useAccount();
  const { connect, connectors, isPending } = useConnect();
  const { disconnectAsync, isPending: isDisconnecting } = useDisconnect();
  const {
    smartAccountAddress,
    isLoadingSafeWallet,
    pimlicoClient,
    publicClient,
    smartAccountClient,
    getSmartAccountClient,
    setSmartAccountAddress,
  } = usePimlico();

  /* Memo */
  const isLoading = useMemo(
    () =>
      isPending ||
      isDisconnecting ||
      isConnecting ||
      isLoadingSafeWallet ||
      (isConnectedWagmi && !smartAccountAddress),
    [
      isPending,
      isDisconnecting,
      isConnecting,
      isLoadingSafeWallet,
      isConnectedWagmi,
      smartAccountAddress,
    ],
  );

  /* Callbacks */
  const login = useCallback(async () => {
    connect({ connector: connectors[0] });
  }, [connect, connectors]);

  const logout = useCallback(async () => {
    try {
      await queryClient.cancelQueries();

      logOut();
      clearAppCache();

      if (magic?.user?.logout) {
        try {
          await magic.user.logout();
        } catch (magicError) {
          console.warn('Error logging out from Magic SDK:', magicError);
        }
      }

      try {
        await disconnectAsync();
        setSmartAccountAddress(undefined);
      } catch (disconnectError) {
        console.warn('Error disconnecting wallet:', disconnectError);
      }

      queryClient.clear();
      queryClient.removeQueries();
      queryClient.invalidateQueries();
      queryClient.setQueriesData({}, undefined);

      await new Promise((resolve) => setTimeout(resolve, 100));
      window.location.reload();
    } catch (error) {
      console.error('Error during logout process: ', error);
      window.location.reload();
    }
  }, [queryClient, logOut, magic, setSmartAccountAddress, disconnectAsync]);

  /* Memo */
  const isConnected = useMemo(() => {
    return isConnectedWagmi && !!smartAccountAddress && !isLoadingSafeWallet;
  }, [isConnectedWagmi, smartAccountAddress, isLoadingSafeWallet]);

  /* Return */
  return (
    <WalletProviderContext.Provider
      value={{
        smartAccountAddress,
        isLoadingSafeWallet,
        pimlicoClient,
        publicClient,
        smartAccountClient,
        isLoading,
        isLoggedIn,
        isConnected,
        getSmartAccountClient,
        setSmartAccountAddress,
        logout,
        login,
      }}
    >
      {children}
    </WalletProviderContext.Provider>
  );
};
